// Probador: un modelo del gateway con las herramientas de los conectores, como lo vería un agente. Sirve para
// comprobar que las conexiones responden y que un modelo entiende las herramientas antes de darle una llave a nadie.
import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AgentMessage, Button, ChatComposer, PageHead, Select, Vacio } from '@glubi/ds';
import { api, mensajeDeError } from '../lib/api';
import { usd } from '../lib/formato';
import { useSesion } from '../lib/sesion';
import { AvisoSoloVer, Casilla } from '../componentes/Comunes';

interface Paso { nombre: string; argumentos: unknown; resultado: string; error: boolean }
interface Turno { rol: 'user' | 'assistant'; texto: string; pasos?: Paso[]; costo?: number; destino?: string; error?: boolean }

export function Probador() {
  const { cuenta } = useSesion();
  const modelos = useQuery({ queryKey: ['modelos'], queryFn: () => api<any[]>('/api/modelos') });
  const herramientas = useQuery({ queryKey: ['herramientas'], queryFn: () => api<any[]>('/api/herramientas') });
  const [modelo, setModelo] = useState('');
  const [elegidas, setElegidas] = useState<string[]>([]);
  const [sistema, setSistema] = useState('Eres el asistente del negocio. Responde corto y en español. Usa las herramientas para consultar datos reales; no inventes cifras.');
  const [texto, setTexto] = useState('');
  const [turnos, setTurnos] = useState<Turno[]>([]);

  const activo = modelo || modelos.data?.find((m) => m.estado === 'activo')?.nombre || '';
  const enviar = useMutation({
    mutationFn: (historial: Turno[]) => api<{ respuesta: string; pasos: Paso[]; costo: number; proveedor?: string; destino?: string }>('/api/probar', {
      modelo: activo, herramientas: elegidas,
      mensajes: [...(sistema.trim() ? [{ role: 'system', content: sistema }] : []), ...historial.filter((t) => !t.error).map((t) => ({ role: t.rol, content: t.texto }))],
    }),
    onSuccess: (r) => setTurnos((t) => [...t, { rol: 'assistant', texto: r.respuesta, pasos: r.pasos, costo: r.costo, destino: r.proveedor ? `${r.proveedor} · ${r.destino}` : undefined }]),
    onError: (e) => setTurnos((t) => [...t, { rol: 'assistant', texto: mensajeDeError(e), error: true }]),
  });

  const mandar = () => {
    if (!texto.trim() || !activo || enviar.isPending) return;
    const historial = [...turnos, { rol: 'user' as const, texto: texto.trim() }];
    setTurnos(historial);
    setTexto('');
    enviar.mutate(historial);
  };

  if (!cuenta?.puedeAdministrar) return <div className="page"><PageHead title="Probador" /><AvisoSoloVer /></div>;
  if (modelos.data && !modelos.data.length) return <div className="page"><PageHead title="Probador" /><Vacio titulo="Primero un modelo">El probador usa los modelos del gateway. Crear uno en «Modelos».</Vacio></div>;

  return (
    <div className="page">
      <PageHead title="Probador" subtitle="Un modelo del gateway con las herramientas que se elijan. Cada llamada es real, cuesta lo que cuesta y queda en el registro.">
        <Button variant="ghost" icon="refresh" disabled={!turnos.length} onClick={() => setTurnos([])}>Empezar de nuevo</Button>
      </PageHead>
      <div className="un-tercio">
        <div className="stack">
          <Select label="Modelo" value={activo} onChange={(e) => setModelo(e.target.value)} options={(modelos.data ?? []).map((m) => ({ value: m.nombre as string, label: m.deGlubi ? `${m.nombre} · de Glubi` : m.nombre as string }))} />
          <label className="stack" style={{ gap: 6 }}>
            <span className="etiqueta">Instrucciones del sistema</span>
            <textarea className="field" rows={4} value={sistema} onChange={(e) => setSistema(e.target.value)} />
          </label>
          <div className="stack" style={{ gap: 6 }}>
            <span className="etiqueta">Herramientas que puede usar</span>
            <div className="casillas" style={{ maxHeight: 300 }}>
              {herramientas.isLoading && <span className="muted" style={{ fontSize: 13 }}>Preguntando a los conectores…</span>}
              {!herramientas.isLoading && !herramientas.data?.length && <span className="muted" style={{ fontSize: 13 }}>No hay conectores con herramientas.</span>}
              {(herramientas.data ?? []).map((h) => (
                <Casilla key={h.name} checked={elegidas.includes(h.name)} onChange={(v) => setElegidas(v ? [...elegidas, h.name] : elegidas.filter((x) => x !== h.name))}>
                  <code>{h.name}</code>{!h.annotations?.readOnlyHint && <span style={{ color: 'var(--amber-text)', fontSize: 12 }}> · puede escribir</span>}
                </Casilla>
              ))}
            </div>
          </div>
        </div>
        <div className="card" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14, minHeight: 420 }}>
          <div className="chat" style={{ flex: 1 }}>
            {!turnos.length && <div className="muted" style={{ fontSize: 14 }}>Preguntar algo que necesite los sistemas conectados: «¿cuánto vendimos ayer?», «¿qué producto está por agotarse?».</div>}
            {turnos.map((t, i) => (t.rol === 'user' ? <AgentMessage key={i} from="negocio">{t.texto}</AgentMessage> : (
              <div key={i} className="stack" style={{ gap: 6 }}>
                {t.pasos?.map((p, j) => (
                  <div key={j} className={`paso ${p.error ? 'error' : ''}`}>
                    <b style={{ fontWeight: 500 }}>{p.nombre}</b> <code>{JSON.stringify(p.argumentos)}</code>
                    <div className="muted" style={{ marginTop: 4, whiteSpace: 'pre-wrap', maxHeight: 120, overflow: 'auto' }}>{p.resultado}</div>
                  </div>
                ))}
                <div style={{
                  background: t.error ? '#F7E6E4' : 'var(--surface-card)', color: t.error ? 'var(--state-error)' : 'var(--text-primary)', border: '1px solid var(--border-default)',
                  borderRadius: 'var(--radius-md)', padding: '12px 16px', fontSize: 15, lineHeight: '22px', whiteSpace: 'pre-wrap', maxWidth: 560,
                }}>{t.texto}</div>
                {t.destino && <div className="muted" style={{ fontSize: 12 }}>{t.destino} · {usd(t.costo)}</div>}
              </div>
            )))}
            {enviar.isPending && <span className="typing"><span /><span /><span /></span>}
          </div>
          <ChatComposer value={texto} onChange={(e) => setTexto(e.target.value)} onSend={mandar} disabled={enviar.isPending || !activo} placeholder="Escribir un mensaje de prueba…" />
        </div>
      </div>
    </div>
  );
}
